import { InlineWarningComponent } from 'common/warning/warning-inline.js';
import { useContext, useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'redux-state/configure-store.js';
import { IRepoState } from 'redux-state/rootReducer.js';
import { REDUX_WEATHER_WARNINGS_FETCH_INIT } from 'redux-state/weather-warnings/reducer.js';
import { ILocationWarningType, IWarning } from 'redux-state/weather-warnings/types.js';
import { WarningSeverityContext } from 'utilities/weather-warnings/context/context-warning-severity.js';
import { findHighestSeverityLevel } from 'utilities/weather-warnings/warning-severity.js';
import { getLoadedLocationWarnings } from 'utilities/weather-warnings/warnings.js';

interface IProps {
  location: ILocationWarningType;
}

/**  Builds inline warning components based on page location object */
export const InlineWarningFactory = ({ location }: IProps) => {
  const { setWarningSeverity } = useContext(WarningSeverityContext);
  const appDispatch = useAppDispatch();
  const warningsState = useAppSelector((state: IRepoState) => state.WEATHER_WARNINGS.warnings);
  const [warnings, setWarnings] = useState<IWarning[]>([]);
  const [uniqueLocation, setUniqueLocation] = useState(location);

  useEffect(() => {
    if (`${location.type}${location.key}` !== `${uniqueLocation.type}${uniqueLocation.key}`) {
      setUniqueLocation(location);
    }
  }, [location, uniqueLocation]);

  useEffect(() => {
    if (uniqueLocation && warningsState) {
      const warningState = getLoadedLocationWarnings(uniqueLocation, warningsState);
      const warnings = warningState?.warnings;
      if (warnings?.length) {
        if (setWarningSeverity) {
          const highestWarningSeverity = findHighestSeverityLevel(warnings.map(({ warnLevel }) => warnLevel));
          setWarningSeverity(highestWarningSeverity);
        }
      }
      setWarnings(warnings ?? []);
    } else {
      setWarnings([]);
    }
  }, [uniqueLocation, warningsState, setWarningSeverity]);

  useEffect(() => {
    appDispatch(REDUX_WEATHER_WARNINGS_FETCH_INIT({ warningProps: uniqueLocation, timestamp: Date.now() }));
  }, [uniqueLocation, appDispatch]);

  /* @TODO THIS NEEDS A MODIFIER FOR WHEN IT'S IN THE SIDEBAR SLOT InlineWarning--compact */
  return (
    <div className="u-mT-sm u-mB-sm" data-card-name="warning">
      {warnings.map((warning, index) => (
        <InlineWarningComponent key={index} hideDate={location.hideDate} warning={warning} />
      ))}
    </div>
  );
};
